/**
 * PromptPayQRDialog — ReservePay Custom UI Integration
 *
 * Renders a PromptPay QR code inside the app (no redirect to ReservePay page).
 * Flow:
 *   1. selectPaymentMethod("PROMPTPAY") via SDK → payment_session_id
 *   2. POST /api/payment/user/{type}/initiate-reservepay-flow → payment_id
 *   3. SDK polls discover-next-action → DISPLAY_QR → show QR
 *   4. SDK polls until CHECK_STATUS → SUCCESSFUL / FAILED
 *   5. Notify parent via onSuccess / onFail
 */

import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react'
import { useReservePay } from '@/src/hooks/useReservePay'
import {
  initiateCreditReservePayFlow,
  initiatePackageReservePayFlow,
} from '@/lib/api/reservepay-payment'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DialogState = 'selecting' | 'initiating' | 'qr' | 'success' | 'failed'

interface PromptPayQRDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Amount in THB */
  amount: number
  customerEmail?: string
  customerMobile?: string
  customerName?: string
  type: 'credit' | 'package'
  /** Required when type === 'package' */
  packageId?: string
  activationChoice?: 'immediate' | 'queued'
  /** Called with internal transaction ID on success */
  onSuccess: (transactionId: string, creditAmount?: number) => void
  onFail?: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PromptPayQRDialog({
  open,
  onOpenChange,
  amount,
  customerEmail,
  customerMobile,
  customerName,
  type,
  packageId,
  activationChoice = 'queued',
  onSuccess,
  onFail,
}: PromptPayQRDialogProps) {
  const [state, setState] = useState<DialogState>('selecting')
  const [qrString, setQrString] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [creditAmount, setCreditAmount] = useState<number | undefined>(undefined)

  const abortRef = useRef<AbortController | null>(null)
  const { selectPromptPay, waitForQR, waitForCompletion } = useReservePay()

  // ── Kick off flow when dialog opens ────────────────────────────────────────
  useEffect(() => {
    if (!open) return

    // Reset state on each open
    setState('selecting')
    setQrString(null)
    setErrorMsg(null)
    setCreditAmount(undefined)

    const abort = new AbortController()
    abortRef.current = abort

    ;(async () => {
      try {
        // Step 1: SDK selectPaymentMethod
        setState('selecting')
        const { paymentSessionId } = await selectPromptPay(amount, {
          email: customerEmail,
          mobile: customerMobile,
          name: customerName,
        })
        if (abort.signal.aborted) return

        // Step 2: Backend initiate-payment-flow
        setState('initiating')
        let txId: string
        let creditAmt: number | undefined

        if (type === 'credit') {
          const res = await initiateCreditReservePayFlow(paymentSessionId, amount)
          txId = res.data.transaction.id
          creditAmt = res.data.transaction.credit_amount
        } else {
          if (!packageId) throw new Error('packageId is required for package payments')
          const res = await initiatePackageReservePayFlow(paymentSessionId, packageId, activationChoice)
          txId = res.data.transaction.id
        }
        if (abort.signal.aborted) return

        setCreditAmount(creditAmt)

        // Step 3: Wait for QR
        const { qrString: qr } = await waitForQR(paymentSessionId, abort.signal)
        if (abort.signal.aborted) return
        setQrString(qr)
        setState('qr')

        // Step 4: Wait for completion (duals: SDK polling + webhook-status polling)
        const finalStatus = await waitForCompletion(paymentSessionId, txId, abort.signal)
        if (abort.signal.aborted) return

        if (finalStatus === 'SUCCESSFUL') {
          setState('success')
          onSuccess(txId, creditAmt)
        } else {
          setState('failed')
          setErrorMsg('การชำระเงินไม่สำเร็จ')
          onFail?.()
        }
      } catch (err: any) {
        if (abort.signal.aborted) return
        console.error('[PromptPayQRDialog] error:', err)
        setState('failed')
        setErrorMsg(err.message || 'เกิดข้อผิดพลาด')
        onFail?.()
      }
    })()

    return () => {
      abort.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── Handle manual close ─────────────────────────────────────────────────────
  function handleClose() {
    abortRef.current?.abort()
    onOpenChange(false)
  }

  // ── Render helpers ──────────────────────────────────────────────────────────
  function renderBody() {
    switch (state) {
      case 'selecting':
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="w-10 h-10 animate-spin text-[#7FB069]" />
            <p className="text-gray-600 text-sm">กำลังเตรียม PromptPay...</p>
          </div>
        )

      case 'initiating':
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="w-10 h-10 animate-spin text-[#7FB069]" />
            <p className="text-gray-600 text-sm">กำลังสร้างรายการชำระเงิน...</p>
          </div>
        )

      case 'qr':
        return (
          <div className="flex flex-col items-center gap-5">
            {/* Amount */}
            <div className="text-center">
              <p className="text-sm text-gray-500">จำนวนเงิน</p>
              <p className="text-3xl font-bold text-[#7FB069]">
                ฿{amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </p>
              {type === 'credit' && creditAmount && (
                <p className="text-sm text-gray-500 mt-0.5">
                  ≈ {creditAmount.toLocaleString()} เครดิต
                </p>
              )}
            </div>

            {/* QR Code */}
            {qrString && (
              <div className="p-4 bg-white rounded-2xl border-2 border-[#7FB069]/30 shadow-sm">
                <QRCodeSVG value={qrString} size={220} level="M" />
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-2 rounded-lg border border-amber-200">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>สแกน QR ด้วยแอปธนาคาร แล้วรอระบบยืนยัน</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>รอการยืนยันการชำระเงิน...</span>
            </div>
          </div>
        )

      case 'success':
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
            <div className="text-center">
              <p className="text-xl font-bold text-green-700">ชำระเงินสำเร็จ!</p>
              {type === 'credit' && creditAmount && (
                <p className="text-gray-600 mt-1">
                  เติม {creditAmount.toLocaleString()} เครดิตเรียบร้อยแล้ว
                </p>
              )}
              {type === 'package' && (
                <p className="text-gray-600 mt-1">แพ็กเกจของคุณจะเริ่มใช้งานเร็วๆ นี้</p>
              )}
            </div>
            <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700 text-white">
              เสร็จสิ้น
            </Button>
          </div>
        )

      case 'failed':
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <XCircle className="w-16 h-16 text-red-500" />
            <div className="text-center">
              <p className="text-xl font-bold text-red-700">ชำระเงินไม่สำเร็จ</p>
              {errorMsg && <p className="text-gray-600 mt-1 text-sm">{errorMsg}</p>}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose}>
                ปิด
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false)
                  // Brief delay so parent can re-open
                  setTimeout(() => onOpenChange(true), 100)
                }}
                className="bg-[#7FB069] hover:bg-[#7FB069]/90 text-white"
              >
                ลองใหม่
              </Button>
            </div>
          </div>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent
        className="max-w-sm w-full"
        onInteractOutside={(e) => {
          // Prevent accidental close while QR is displayed
          if (state === 'qr' || state === 'selecting' || state === 'initiating') {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-center">
            {state === 'success'
              ? 'ชำระเงินสำเร็จ'
              : state === 'failed'
              ? 'ชำระเงินไม่สำเร็จ'
              : 'ชำระด้วย PromptPay'}
          </DialogTitle>
        </DialogHeader>

        {renderBody()}

        {/* Allow cancel only in non-terminal, non-qr states */}
        {(state === 'selecting' || state === 'initiating') && (
          <div className="flex justify-center mt-2">
            <Button variant="ghost" size="sm" onClick={handleClose} className="text-gray-400">
              ยกเลิก
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
